'use client';

import type {FormEvent} from 'react';
import {
	createRef,
	type FC,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import clsx from 'clsx';

import {buttonClasses} from '@/components/button';
import {FadeInDownDiv} from '@/components/framer/fade-in-down-div';
import {CheckIcon} from '@/components/icons/check';
import {FormInput} from '@/components/input/form';
import {Spinner} from '@/components/spinner';
import type {SiteSettingsQueryResult} from '@/schemas/types';
import {i18n} from '@/utils/i18n';
import {NEWSLETTER_FORM_ID, THEME_DARK} from '@/utils/types';

import {NewsletterDisclosure} from './newsletterDisclosure';

const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
const recaptchaRef = createRef<ReCAPTCHA>();

interface Props {
	siteSettings?: SiteSettingsQueryResult;
}

enum Status {
	IDLE,
	WORKING,
	ERROR,
	SUCCESS,
}

export const Newsletter: FC<Props> = ({siteSettings}) => {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [emailAddress, setEmailAddress] = useState('');

	const [status, setStatus] = useState(Status.IDLE);
	const [message, setMessage] = useState('');
	const container = useRef<HTMLFormElement | null>(null);

	const handleSubmit = useCallback(
		async (e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			setStatus(Status.WORKING);
			setMessage('');

			const data = JSON.stringify({
				emailAddress: emailAddress,
				status: 'subscribed',
				firstName: firstName,
				lastName: lastName,
			});

			const recaptchaToken = await recaptchaRef.current?.executeAsync();
			const response = await fetch('/api/newsletter-signup', {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					'g-recaptcha-token': recaptchaToken ?? '',
				},
				body: data,
			});

			if (!response.ok) {
				setStatus(Status.ERROR);
				if (response.status === 409) {
					setMessage(i18n.components.newsletter.alreadySignedUp);
				} else {
					setMessage(i18n.components.newsletter.signupError);
				}
				return;
			}

			setStatus(Status.SUCCESS);
			setMessage(i18n.components.newsletter.signupSuccessful);
		},
		[firstName, lastName, emailAddress],
	);

	useEffect(() => {
		if (status === Status.SUCCESS) {
			// FIXME: fix react hook error
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setFirstName('');
			setLastName('');
			setEmailAddress('');
			setTimeout(() => {
				setMessage('');
				setStatus(Status.IDLE);
			}, 3000);
		}
	}, [status]);

	const newsletter = siteSettings?.newsletter;
	if (!newsletter) {
		return null;
	}

	const {title, consentText, buttonText} = newsletter;

	return (
		<div
			className={clsx(
				'relative',
				'md:col-start-2',
				'md:row-start-2',
				'xl:max-w-xl',
			)}
		>
			<h2
				className={clsx(
					'text-xl',
					'font-bold',
					'mb-4',
					'md:font-medium',
					'md:text-2xl',
				)}
			>
				{title}
			</h2>

			{status === Status.SUCCESS && (
				<FadeInDownDiv
					delay={0.3}
					className={clsx('inline-block', 'mt-8')}
				>
					<div
						className={clsx(
							'flex',
							'items-center',
							'justify-center',
							'h-full',
							'flex-col',
							'gap-6',
						)}
					>
						<div className={clsx('text-green-700', 'h-10', 'w-10')}>
							<CheckIcon />
						</div>
						<div>{message}</div>
					</div>
				</FadeInDownDiv>
			)}

			{status !== Status.SUCCESS && (
				<FadeInDownDiv delay={0.3}>
					<form
						className={clsx('flex', 'flex-col', 'gap-6')}
						action="#"
						method="POST"
						onSubmit={handleSubmit}
						autoComplete="on"
						ref={container}
					>
						<div>
							<FormInput
								id={NEWSLETTER_FORM_ID}
								theme={THEME_DARK}
								name="firstName"
								className={clsx('md:text-lg', 'md:font-normal')}
								placeholder="Fornavn..."
								onChange={(e) => {
									setFirstName(e.target.value);
								}}
								value={firstName}
								required
								disabled={status === Status.WORKING}
							/>
							<FormInput
								theme={THEME_DARK}
								name="lastName"
								className={clsx('md:text-lg', 'md:font-normal')}
								placeholder="Etternavn..."
								onChange={(e) => {
									setLastName(e.target.value);
								}}
								value={lastName}
								required
								disabled={status === Status.WORKING}
							/>
							<FormInput
								theme={THEME_DARK}
								name="emailAddress"
								className={clsx('md:text-lg', 'md:font-normal')}
								placeholder="E-post..."
								type="email"
								onChange={(e) => {
									setEmailAddress(e.target.value);
								}}
								value={emailAddress}
								required
								disabled={status === Status.WORKING}
							/>
						</div>
						<div
							className={clsx(
								'text-orange-700',
								'flex',
								'items-center',
								'gap-6',
							)}
						>
							{message}
						</div>
						<div
							className={clsx(
								'flex',
								'gap-2',
								'justify-end',
								'items-center',
							)}
						>
							<NewsletterDisclosure
								className={'flex-1'}
								theme={THEME_DARK}
							>
								{consentText}
							</NewsletterDisclosure>
							{status === Status.WORKING && <Spinner />}
							<button
								className={clsx(
									buttonClasses,
									'items-center',
									'gap-4',
									'!text-bodyMedium',
									'rounded-[3.5rem]',
									'text-turquoise-700',
									'border',
									'border-turquoise-700',
									'focus-visible:ring-turquoise-700',
									'focus-visible:ring-offset-forestGreen-700',
									'h-9',
									'md:h-14',
								)}
								disabled={status === Status.WORKING}
							>
								{buttonText}
							</button>
						</div>

						{siteKey && (
							<ReCAPTCHA
								ref={recaptchaRef}
								sitekey={siteKey}
								size="invisible"
							/>
						)}
					</form>
				</FadeInDownDiv>
			)}
		</div>
	);
};
